import { Hairline, Loading, Message } from "@components";
import { ForEach, If } from "@components/control-statements";
import { Unit } from "@core/Unit";
import { useEffect, useRef, useState } from "react";

import { OlgaSources } from "@tools/OlgaSources";

import { useIsInViewport } from "@hooks";
import { Currency } from "@utils/Currency";

import OcdpAPI from "@providers/OcdpAPI";

import styles from "@styles/containers/oraculo/customer-drawer/history-tab.module.scss";
import { Numbers } from "@utils/Numbers";
import { Percentage } from "@utils/Percentage";
import classNames from "classnames/bind";

import { Benefit } from "@core/Benefit";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isToday from "dayjs/plugin/isToday";
import OrderMarkups from "./OrderMarkups";

dayjs.extend(isToday);
dayjs.extend(customParseFormat);

const css = classNames.bind(styles);

const Icon = ({ field }) => {
  const icons = {
    name: <i className="fal fa-user" />,
    birthdate: <i className="fal fa-cake-candles" />,
    email: <i className="fal fa-envelope" />,
    cpf: <i className="fal fa-card-id" />,
    addresses: <i className="fal fa-location-dot" />,
  };

  return icons[field];
};

const CLIMATES = {
  "clear-day": { label: "Dia ensolarado", icon: "fa-sun", color: "sun" },
  "clear-night": {
    label: "Noite aberta",
    icon: "fa-moon-stars",
    color: "cold",
  },
  rain: { label: "Chuvoso", icon: "fa-cloud-rain", color: "cold" },
  cloudy: { label: "Nublado", icon: "fa-clouds", color: "cloudy" },
  "partly-cloudy-day": {
    label: "Dia parcialmente nublado",
    icon: "fa-clouds-sun",
    color: "cloudy",
  },
  "partly-cloudy-night": {
    label: "Noite parcialmente nublada",
    icon: "fa-clouds-moon",
    color: "cloudy",
  },
  fog: { label: "Neblina", icon: "fa-cloud-fog", color: "cloudy" },
  wind: { label: "Ventania", icon: "fa-wind", color: "cold" },
  undefined: {
    label: "Indefinido",
    icon: "fa-cloud-question",
    color: "cloudy",
  },
};

const OrderItem = ({ order, index, loadCustomer }) => {
  const [product_collapse, setProductCollapsed] = useState(true);

  console.log(order)

  const source = OlgaSources.getSourceByAlias(order.source);
  const origin = getExternalSourceName(order.source, order.origin);

  const total = Currency.formatCurrency(+order.order_total || 0);
  const source_ribbon = `${order.unit_name} - ${order.brand_name} - ${
    origin || "Origem Desconhecida"
  } - ${source?.name || "Fonte Desconhecida"}`;
  const temperature = order.climate_temperature
    ? Numbers.formatFloat(order.climate_temperature, 2)
    : "--";
  const humidity =
    order.climate_humidity !== null
      ? `${Percentage.format(order.climate_humidity)}%`
      : "---";
  const precipitation =
    order.climate_precipitation !== null
      ? `${Percentage.format(order.climate_precipitation)} mm`
      : "---";
  const climate =
    (order.climate_condition && CLIMATES[order.climate_condition]) ||
    CLIMATES.undefined;
  const created_at = dayjs(order.order_created_at);

  const discount_total = order?.order_discounts?.total
    ? Number(order?.order_discounts?.total) || 0
    : 0;
  const products = Array.isArray(order?.order_products)
    ? order?.order_products
    : [];

  return (
    <div className={styles.event}>
      <div className={styles.record} key={index}>
        <div className={styles.header}>
          <div className="d-flex align-center justify-between">
            <div className="d-flex flex-column">
              <p className={styles.eventName}>Pedido</p>
              <div className="d-flex align-center">
                <p className={styles.date}>{created_at.format("DD/MM/YYYY")}</p>
                <p className={styles.time}>
                  &nbsp;-&nbsp;{created_at.format("HH:mm")}
                </p>
              </div>
            </div>
            <span className={styles.eventValue} title={total}>
              {total}
            </span>
          </div>
        </div>

        {created_at.isToday() && (
          <Message type="warning" className="mb-10">
            Em processamento. este pedido não será considerado em consulta
          </Message>
        )}

        <If cond={discount_total > 0}>
          <div className={styles.benefit}>
            <i className="fal fa-gift" />
            <p>
              Desconto de{" "}
              <strong>{Currency.formatCurrency(discount_total)}</strong>
            </p>
          </div>
        </If>
        <If cond={products?.length > 0}>
          <div className={styles.orderContainer}>
            <div className={styles.order}>
              <div
                style={{
                  overflow: product_collapse ? "hidden" : "auto",
                  maxHeight: product_collapse ? "300px" : "100%",
                }}
                onClick={() => setProductCollapsed(!product_collapse)}
              >
                {products?.map((p) => {
                  return (
                    <>
                      <span>
                        {p.quantity || 1}x {p.name}{" "}
                        {p.total > 0 && `(${Currency.formatCurrency(p.total)})`}
                      </span>
                      <If cond={p.size_name}>
                        <span>- {p.size_name}</span>
                      </If>
                      <br />
                    </>
                  );
                })}
              </div>
            </div>
          </div>
        </If>

        <OrderMarkups order={order} loadCustomer={loadCustomer} />

        <Hairline className="mt-10 mb-10" />

        <div className={styles.climate}>
          <div className={styles.temperature}>
            <div className={css({ [climate.color]: true })}>
              <i className={`fas ${climate.icon}`} />
            </div>

            <p className={styles.value}>{temperature}</p>
            <span className={styles.celsius}>ºC</span>
          </div>
          <div className={styles.clime}>
            <p className={styles.type}>{climate.label}</p>
            <span>Umidade: {humidity}</span>
            <span>Quantidade de chuva: {precipitation}</span>
          </div>
        </div>

        <div className={styles.source}>
          <p title={source_ribbon}>{source_ribbon}</p>
        </div>
      </div>
    </div>
  );
};

const CouponItem = ({ coupon, index, programByPoint }) => {
  const total = Benefit.getDiscountText(coupon, coupon.order_amount);
  const source_ribbon = `${coupon.unit_name} - ${coupon.brand_name}`;
  const used_at = dayjs(coupon.used_at);
  const expires_at = dayjs(coupon.expires_at);

  return (
    <div className={styles.event}>
      <div className={styles.record} key={index}>
        <div className={styles.header}>
          <div className="d-flex align-center justify-between">
            <div className="d-flex flex-column">
              <p className={styles.eventName}>Uso de cupom</p>
              <div className="d-flex align-center">
                <p className={styles.date}>{used_at.format("DD/MM/YYYY")}</p>
                <p className={styles.time}>
                  &nbsp;-&nbsp;{used_at.format("HH:mm")}
                </p>
              </div>
            </div>
            <span className={styles.eventValue} title={total}>
              {total}
            </span>
          </div>
        </div>
        <div className={styles.couponDetails}>
          <div className={styles.couponHeader}>
            <p className={styles.couponValue}>
              {Benefit.getTitle(coupon, programByPoint)}
            </p>
            <div className="d-flex align-center">
              <div className={styles.couponGift}>
                <i className="fas fa-gift" />
              </div>
              <div className={styles.couponPin}>
                {`${coupon.code}`.toUpperCase()}
              </div>
            </div>
          </div>
          <div className="d-flex align-center justify-between">
            <span className={styles.couponExp}>Expirava em</span>
            <span className={styles.couponExpDate}>
              {expires_at.format("DD/MM/YYYY")}
            </span>
          </div>
        </div>

        <div className={styles.source}>
          <p title={source_ribbon}>{source_ribbon}</p>
        </div>
      </div>
    </div>
  );
};

const EvaluationItem = ({ evaluation, index }) => {
  const created_at = dayjs(evaluation.created_at);

  const renderItems = () => {
    const evaluation_items = [
      { key: "product_rate", label: "Produto" },
      { key: "delivery_rate", label: "Entrega" },
      { key: "service_rate", label: "Atendimento" },
      { key: "experience_rate", label: "Experiência" },
      { key: "environment_rate", label: "Ambiente" },
    ];

    if (evaluation.criteria) {
      evaluation_items = evaluation.criteria.map((c) => ({
        key: c.nick,
        label: c.name,
      }));
      for (let c of evaluation.criteria) {
        evaluation[c.nick] = c.value;
      }
    }

    return (
      <div className={styles.evaluationContainer}>
        <div className={styles.evaluation}>
          <div
            style={{
              overflow: "auto",
              maxHeight: "100%",
            }}
          >
            {evaluation_items?.map((p) => {
              return (
                <div className="d-flex justify-between">
                  <span>{p.label}</span>
                  <span>
                    {!evaluation[p.key] ? (
                      "-"
                    ) : (
                      <>
                        {evaluation[p.key]} <i className="fa fa-star"></i>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.event}>
      <div className={styles.record} key={index}>
        <div className={styles.header}>
          <div className="d-flex align-center justify-between">
            <div className="d-flex flex-column">
              <p className={styles.eventName}>
                Avaliação - Pedido #{evaluation.display_id}
              </p>
              <div className="d-flex align-center">
                <p className={styles.date}>{created_at.format("DD/MM/YYYY")}</p>
                <p className={styles.time}>
                  &nbsp;-&nbsp;{created_at.format("HH:mm")}
                </p>
              </div>
            </div>
            <span
              className={styles.eventValue}
              title={evaluation.overall_average}
            >
              {evaluation.overall_average}&nbsp;<i className="fa fa-star"></i>
            </span>
          </div>
        </div>
        {renderItems()}
        <If cond={evaluation.comment}>
          <div className={styles.orderContainer}>
            <div className={styles.order}>
              <div
                style={{
                  overflow: "auto",
                  maxHeight: "100%",
                }}
              >
                {evaluation.comment}
              </div>
            </div>
          </div>
        </If>
      </div>
    </div>
  );
};

export const HistoryTab = ({ customer, brandId, unitId, loadCustomer }) => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [next_page, setNextPage] = useState({});

  const loadMoreData = async () => {
    if (!next_page) {
      return;
    }

    const ocdp = new OcdpAPI();
    const response = await ocdp.events(customer.id, {
      ...next_page,
      brand_id: brandId,
      unit_id: unitId,
    });
    setEvents([...events, ...(response.items || [])]);
    setNextPage(response.next?.page != -1 ? response.next : null);
  };

  const init = async () => {
    setLoading(true);

    await Promise.all([
      await OlgaSources.install(),
      await Unit.install(),
      await loadMoreData(),
    ]);

    setLoading(false);
  };

  useEffect(() => {
    if (customer.id) {
      init();
    }
  }, [customer]);

  return (
    <>
      <If cond={loading}>
        <div className={styles.loading}>
          <Loading color="#6161ff" />
        </div>
      </If>
      <If cond={!loading && events.length > 0}>
        <Events events={events} loadCustomer={loadCustomer} />
      </If>
      <If cond={!loading && events.length == 0}>
        <div className={styles.empty}>
          <i className="fal fa-list-timeline" />
          <p>Não há nenhum evento para ser exibido aqui.</p>
        </div>
      </If>
    </>
  );
};

const Events = ({ events, onLoadMoreData, loadCustomer }) => {
  const [loading, setLoading] = useState(false);
  const list_end_ref = useRef(null);
  const is_visible = useIsInViewport(list_end_ref);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      onLoadMoreData && (await onLoadMoreData());
      setLoading(false);
    };

    is_visible && load();
  }, [is_visible]);

  const programByPoint =
    localStorage.getItem("cashbackProgramType") === "POINT";

  return (
    <div className={styles.recordsScroll}>
      <ForEach
        items={events || []}
        render={(item, index) => {
          switch (item.event_type) {
            case "order":
              return (
                <OrderItem
                  order={item.data}
                  index={index}
                  programByPoint={programByPoint}
                  loadCustomer={loadCustomer}
                />
              );
            case "coupon":
              return (
                <CouponItem
                  coupon={item.data}
                  index={index}
                  programByPoint={programByPoint}
                />
              );
            case "evaluation":
              return (
                <EvaluationItem
                  evaluation={item.data}
                  index={index}
                  programByPoint={programByPoint}
                />
              );
            default:
              return null;
          }
        }}
      />
      <If cond={loading}>
        <div className={styles.loading}>
          <div className="w-100 d-flex align-center justify-center flex-1 pt-25 pb-25">
            <Loading />
          </div>
        </div>
      </If>
      <div ref={list_end_ref}>&nbsp;</div>
    </div>
  );
};

const getExternalSourceName = (origin, external_source) => {
  return (
    OlgaSources.externalSources?.[origin]?.[external_source]?.correct ||
    external_source
  );
};

const formatType = (item) => {
  return item.value;
};
